// slotApprovals.ts — the client-side loop behind "Approve N selected" and
// "Approve on behalf" on the v2 review console.
//
// There is NO multi-slot endpoint (design_handoff_npm_timesheet_review errata
// item 6): every approval slot is its own POST, and each of those can report a
// business-rule refusal as a HTTP-200 body `{ success: false, errorMessage }`
// rather than an HTTP error. Treating a 2xx as success is exactly the bug the
// board's bulk approve was fixed for — so the loop is sequential, every slot's
// outcome is captured, and the caller reports partial failures by name.
//
// Errata item 7: the recorded `ApprovalAdminEmployeeID` comes from the request
// body, not the session, so the caller must always pass the SIGNED-IN admin's
// AdminEmployeeID. That is what makes the "approved by X on behalf of Y" display
// truthful; never pass the slot owner's ID.

import {
  approveManagerApproval,
  approveApprovalGroupApproval,
  approveSupervisorApproval,
} from '../api/timesheet';

export type ApprovalSlotKind = 'manager' | 'group' | 'requiredSuper';

/** The minimum a slot must carry to be approvable. `approvalID` is the
 *  requiredManagerApprovalsID / requiredApprovalGroupsID / requiredSuperApprovalsID
 *  the matching endpoint expects; only the required-super endpoint accepts null. */
export interface ApprovalSlotRequest {
  key: string;
  kind: ApprovalSlotKind;
  approvalID: number | null;
  /** Person or approval-group name — used verbatim in the failure report. */
  ownerName: string;
}

export interface ApprovalSlotOutcome {
  slot: ApprovalSlotRequest;
  ok: boolean;
  /** Present only when `ok` is false. */
  reason?: string;
}

/** Pull a server-provided message out of either error shape the approve endpoints
 *  use ({ message } on some handlers, { errorMessage } on others). */
export function approvalErrorMessage(err: unknown): string | undefined {
  const e = err as { response?: { data?: { errorMessage?: string; message?: string } }; message?: string };
  return e?.response?.data?.errorMessage || e?.response?.data?.message || undefined;
}

/** The endpoints return the raw axios response; a refusal is in `response.data`. */
function refusalFromResponse(response: unknown): string | null {
  const body = (response as { data?: { success?: boolean; errorMessage?: string } } | undefined)?.data;
  if (body && body.success === false) {
    return body.errorMessage || 'Approval was rejected.';
  }
  return null;
}

async function approveOneSlot(
  timesheetID: number,
  adminEmployeeID: number,
  slot: ApprovalSlotRequest
): Promise<ApprovalSlotOutcome> {
  try {
    if (slot.kind !== 'requiredSuper' && !slot.approvalID) {
      // A manager / group slot with no ID cannot be targeted — surface it rather
      // than POSTing an approval the server would attach to the wrong row.
      return { slot, ok: false, reason: 'This approval slot is missing its ID — refresh and try again.' };
    }

    let response: unknown;
    if (slot.kind === 'manager') {
      response = await approveManagerApproval(timesheetID, adminEmployeeID, slot.approvalID as number);
    } else if (slot.kind === 'group') {
      response = await approveApprovalGroupApproval(timesheetID, adminEmployeeID, slot.approvalID as number);
    } else {
      response = await approveSupervisorApproval(timesheetID, adminEmployeeID, slot.approvalID ?? undefined);
    }

    const refusal = refusalFromResponse(response);
    if (refusal) return { slot, ok: false, reason: refusal };
    return { slot, ok: true };
  } catch (err) {
    return { slot, ok: false, reason: approvalErrorMessage(err) || 'Approval failed.' };
  }
}

/**
 * Approve each slot in order, never stopping at the first failure — a slot that
 * refuses must not silently swallow the ones behind it. Returns one outcome per
 * requested slot, in the order given.
 */
export async function approveSlotsSequentially(
  timesheetID: number,
  adminEmployeeID: number,
  slots: ApprovalSlotRequest[]
): Promise<ApprovalSlotOutcome[]> {
  const outcomes: ApprovalSlotOutcome[] = [];
  for (const slot of slots) {
    // eslint-disable-next-line no-await-in-loop -- sequential by design: the API
    // flips the timesheet's status once the last slot lands, so concurrent slot
    // approvals on one timesheet would race that transition.
    outcomes.push(await approveOneSlot(timesheetID, adminEmployeeID, slot));
  }
  return outcomes;
}

export interface SlotApprovalSummary {
  approved: ApprovalSlotOutcome[];
  failed: ApprovalSlotOutcome[];
  /** True when at least one slot approved — the caller should refresh either way. */
  anyApproved: boolean;
  /** True when every requested slot approved. */
  allApproved: boolean;
}

/** Split outcomes into what landed and what did not. */
export function summarizeSlotOutcomes(outcomes: ApprovalSlotOutcome[]): SlotApprovalSummary {
  const approved = outcomes.filter((o) => o.ok);
  const failed = outcomes.filter((o) => !o.ok);
  return {
    approved,
    failed,
    anyApproved: approved.length > 0,
    allApproved: outcomes.length > 0 && failed.length === 0,
  };
}
